import { Controller } from "@hotwired/stimulus"

const COOKIE_NAME = "domo_posthog_consent"
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

export default class extends Controller {
  static targets = ["banner"]

  connect() {
    const consent = this.readConsent()

    if (consent === "accepted") {
      this.initPosthog()
    } else if (!consent) {
      this.showBanner()
    }
  }

  accept() {
    this.writeConsent("accepted")
    this.syncServerConsent(true)
    this.hideBanner()
    this.initPosthog()
  }

  decline() {
    this.writeConsent("declined")
    this.syncServerConsent(false)
    this.hideBanner()
  }

  reopen(event) {
    if (event) event.preventDefault()
    this.clearConsent()
    this.showBanner()
  }

  showBanner() {
    if (this.hasBannerTarget) this.bannerTarget.hidden = false
  }

  hideBanner() {
    if (this.hasBannerTarget) this.bannerTarget.hidden = true
  }

  readConsent() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]+)`))
    return match ? decodeURIComponent(match[1]) : null
  }

  writeConsent(value) {
    document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax`
  }

  clearConsent() {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
  }

  // Mirror the consent state onto current_customer.posthog_consent_accepted_at
  // so server-side captures in non-request contexts (AASM callbacks, Sidekiq
  // jobs) can check consent without a cookie. Anonymous users get a silent
  // no-op on the server. Cookie is still set first synchronously, so a
  // failed POST never blocks the cookie from being written.
  syncServerConsent(accepted) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch("/customers/posthog_consent", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...(token ? { "X-CSRF-Token": token } : {}),
      },
      body: JSON.stringify({ accepted: !!accepted }),
    }).catch(() => {
      // Best-effort: the cookie has already been set, so analytics gating
      // from controllers stays correct. The column self-heals on the next
      // accept/decline click.
    })
  }

  initPosthog() {
    const key = document.querySelector('meta[name="posthog-key"]')?.content
    const host = document.querySelector('meta[name="posthog-host"]')?.content || "https://eu.i.posthog.com"
    if (!key) return
    if (window.__posthogInitialized) return
    window.__posthogInitialized = true

    // Load PostHog's array.js loader from their CDN rather than bundling
    // posthog-js through Webpacker. The npm package uses modern syntax that
    // this codebase's Webpack 4 / Babel chain can't parse, and the script
    // is small enough that an extra request beats fighting the tooling.
    const assetHost = host.replace(".i.posthog.com", "-assets.i.posthog.com")
    const script = document.createElement("script")
    script.src = `${assetHost}/static/array.js`
    script.async = true
    script.onload = () => {
      window.posthog.init(key, {
        api_host: host,
        persistence: "localStorage+cookie",
        autocapture: true,
        capture_pageview: true,
        enable_heatmaps: true,
      })
    }
    document.head.appendChild(script)
  }
}
